import React from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import CounselSurveyForm, { buildSurveyAnswerInputs } from '../components/CounselSurveyForm'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'

function unwrap(res) {
  return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
}

export default function PublicCounselSurveyPage({ tokenOverride = '' }) {
  const { token: routeToken } = useParams()
  const token = tokenOverride || routeToken || ''
  const storageKey = `counsel-survey-resume:${token}`
  const [activeToken, setActiveToken] = React.useState(() => {
    try {
      return window.sessionStorage.getItem(storageKey) || token
    } catch {
      return token
    }
  })

  React.useEffect(() => {
    try {
      setActiveToken(window.sessionStorage.getItem(storageKey) || token)
    } catch {
      setActiveToken(token)
    }
  }, [storageKey, token])

  const rememberResumePath = React.useCallback((publicPath) => {
    if (!publicPath) return
    const nextToken = String(publicPath).split('/').filter(Boolean).pop()
    if (!nextToken) return
    setActiveToken(nextToken)
    try {
      window.sessionStorage.setItem(storageKey, nextToken)
    } catch {}
  }, [storageKey])

  const surveyQuery = useQuery({
    queryKey: ['public-counsel-survey', token, activeToken],
    enabled: Boolean(activeToken),
    queryFn: async () => unwrap(await api.get(`/public/counsel-surveys/t/${activeToken}`, { __silent: true })),
  })

  const saveMutation = useMutation({
    mutationFn: async ({ answers, progressPercent }) => unwrap(await api.patch(`/public/counsel-surveys/t/${activeToken}/draft`, {
      progressPercent,
      answers,
    })),
    onSuccess: (data) => {
      try { window?.toast?.show?.('임시저장되었습니다.', 'success') } catch {}
      rememberResumePath(data?.publicPath)
      surveyQuery.refetch()
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '임시저장 실패', 'error') } catch {}
    },
  })

  const submitMutation = useMutation({
    mutationFn: async ({ answers }) => unwrap(await api.post(`/public/counsel-surveys/t/${activeToken}/submit`, {
      progressPercent: 100,
      answers,
    })),
    onSuccess: (data) => {
      try { window?.toast?.show?.('설문이 제출되었습니다. 감사합니다.', 'success') } catch {}
      rememberResumePath(data?.publicPath)
      surveyQuery.refetch()
    },
    onError: (e) => {
      try { window?.toast?.show?.(e?.response?.data?.message || e?.message || '설문 제출 실패', 'error') } catch {}
    },
  })

  if (!token) return <InlineError message="설문 링크가 올바르지 않습니다." />
  if (surveyQuery.isLoading) return <Loading />
  if (surveyQuery.isError) return <InlineError message={surveyQuery.error?.message || '설문을 불러오지 못했습니다.'} />

  const data = surveyQuery.data || {}
  const isSubmitted = data.status === 'SUBMITTED' || data.status === 'ANALYZED'

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <CounselSurveyForm
        schema={data.survey || { title: '고객 설문', sections: [] }}
        initialAnswers={data.answers}
        saving={saveMutation.isPending}
        submitting={submitMutation.isPending}
        saveDisabled={isSubmitted}
        submitDisabled={isSubmitted}
        saveLabel={isSubmitted ? '제출 완료' : '임시저장'}
        submitLabel={isSubmitted ? '제출 완료' : '설문 제출'}
        onSave={async (answerMap, progressPercent) => {
          if (isSubmitted) return
          await saveMutation.mutateAsync({
            progressPercent,
            answers: buildSurveyAnswerInputs(data.survey, answerMap),
          })
        }}
        onSubmit={async (answerMap) => {
          if (isSubmitted) return
          await submitMutation.mutateAsync({
            answers: buildSurveyAnswerInputs(data.survey, answerMap),
          })
        }}
      />
    </div>
  )
}
